import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import {
  Group,
  Card,
  Blockquote,
  Collapse,
  Flex,
  rem,
  Container,
  ScrollArea,
  Checkbox,
  Anchor,
  Center,
  Tooltip,
  ActionIcon,
  Divider,
  MultiSelect,
  Button,
  TextInput,
  Textarea,
  Stack,
  Box,
  Text,
} from '@mantine/core';
import {
  IconArrowLeft,
  IconChartBar,
  IconDeviceFloppy,
  IconExclamationCircle,
  IconFileText,
  IconInfoCircle,
  IconSearch,
  IconTarget,
  IconX,
  IconTrash,
  IconPlus,
  IconCopyPlus,
  IconHelpCircle,
  IconPlayerTrackPrev,
} from '@tabler/icons-react';
import useActiveProcess from '../hooks/useActiveProcess.js';
import useActivePermissions from '../hooks/useActivePermission.js';
import { usePreviousEvidences } from '../hooks/usePreviousEvidences.js';
import { loadMatrixPei } from '../actions/peiActions.js';
import { saveEvidence, loadEvidenceById } from '../actions/evidencesActions.js';

import { notifyError } from '../components/Toast';
import ModalAttachment from '../components/ModalAttachment.js';
import DropzoneButton from '../components/DropzoneButton.js';
import { isProcessActive } from '../utils/processHelpers.js';
import { base64ToFile, fetchFile } from '../utils/fileService.js';

import '../App.css';
import ModalEvidence from '../components/ModalEvidence.js';

const getAllKPIs = (pei) => {
  const result = [];
  pei?.Objetivos?.forEach((objetivo) => {
    objetivo.criterios?.forEach((criterio) => {
      criterio.kpiList?.forEach((kpi) => {
        result.push({
          ...kpi,
          objetivoNumero: objetivo.numObjetivo,
          objetivoDescripcion: objetivo.descripcion,
          criterioId: criterio.Id,
          criterioNumero: criterio.numCriterio,
          criterioDescripcion: criterio.descripcion,
          numeroCompleto: `${objetivo.numObjetivo}.${criterio.numCriterio}.${kpi.numKpi}`,
        });
      });
    });
  });
  return result;
};

const SaveEvidencesPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { evidenceId } = useParams();
  const { user, permissions } = useSelector((state) => state.auth);
  const { data: pei } = useSelector((state) => state.pei.matrixPei);
  const { saving, editingEvidence, loadingEditingEvidence } = useSelector((state) => state.evidence);
  const isEditMode = Boolean(evidenceId);

  const activeProcess = useActiveProcess();
  const activePermission = useActivePermissions();
  const saveEvidenceEnabled = isProcessActive(activeProcess);
  const activeMatrix = activeProcess?.matriz?.Id;

  const allKpis = useMemo(() => getAllKPIs(pei), [pei]);

  const [uiState, setUiState] = useState({ guideOpened: false, searchTerm: '', loadingArchivos: false, });
  const [selectedEvidence, setSelectedEvidence] = useState(null);
  const [evidenceModalOpened, setEvidenceModalOpened] = useState(false);
  const [selectedKpis, setSelectedKpis] = useState([]);
  const [evidenceData, setEvidenceData] = useState({
    titulo: '',
    descripcion: '',
    archivo: null,
    archivoIdPrincipal: null,
    archivoPrincipalCambiado: false,
    anexos: [],
  });

  const [unitsToDuplicate, setUnitsToDuplicate] = useState([]);
  const [attachmentModalOpen, setAttachmentModalOpen] = useState(false);

  const [attachmentsHelpOpen, setAttachmentsHelpOpen] = useState(true);
  const attachmentsHelpText =
    'Un anexo es material complementario (archivos o enlaces) que enriquece la evidencia principal. Podés usarlo para agregar referencias, informes, fotos, manuales, guías, proyectos, programas completos, convenios, artículos u otros recursos relacionados.';

  const { data: previousEvidences, loading: loadingPrevEvidences, error: errorPrevEvidences,} = usePreviousEvidences({
    processId: activeProcess?.Id,
    unidad: activePermission?.permiso?.EntityId?.trim(),
    sede: activePermission?.sede?.codigo?.trim(),
    kpis: selectedKpis,
    debounceMs: 250,
  });

  useEffect(() => {
    if (activeMatrix && !pei) {
      dispatch(loadMatrixPei(activeMatrix, user));
    }
    if (isEditMode && evidenceId) {
      dispatch(loadEvidenceById(evidenceId, user));
    }
  }, [activeMatrix, pei, isEditMode, evidenceId, user, dispatch]);

  useEffect(() => {
    if (editingEvidence && isEditMode) {
      const loadArchivos = async () => {
        setUiState((prev) => ({ ...prev, loadingArchivos: true }));
        try {
          setEvidenceData({
            titulo: editingEvidence.titulo || '',
            descripcion: editingEvidence.descripcion || '',
            archivo: null,
            archivoIdPrincipal: null,
            archivoPrincipalCambiado: false,
            anexos: [],
          });

          const kpiIds = editingEvidence.kpis?.map((kpi) => String(kpi.Id)) || [];
          setSelectedKpis(kpiIds);

          const archivoPrincipal = editingEvidence.archivos?.find((a) => a.esPrincipal);
          if (archivoPrincipal) {
            const archivoBase64 = await fetchFile(archivoPrincipal.Id, user.token);
            if (archivoBase64) {
              const archivoFile = base64ToFile(archivoBase64, archivoPrincipal.nombre, archivoPrincipal.tipoArchivo);
              if (archivoFile) {
                setEvidenceData((prev) => ({
                  ...prev,
                  archivo: archivoFile,
                  archivoIdPrincipal: archivoPrincipal.Id,
                  archivoPrincipalCambiado: false,
                }));
              }
            }
          }

          const anexosPromises =
            editingEvidence.archivos
              ?.filter((archivo) => !archivo.esPrincipal)
              .map(async (archivo) => {
                if (archivo.tipoArchivo === 'url') {
                  return {
                    id: archivo.Id,
                    tipo: 'url',
                    valor: archivo.nombre,
                    nombre: archivo.nombre,
                    descripcion: archivo.descripcion || '',
                    archivoId: archivo.Id,
                  };
                } else {
                  const archivoBase64 = await fetchFile(archivo.Id, user.token);
                  if (archivoBase64) {
                    const archivoFile = base64ToFile(archivoBase64, archivo.nombre, archivo.tipoArchivo);
                    return {
                      id: archivo.Id,
                      tipo: 'archivo',
                      valor: archivoFile,
                      nombre: archivo.nombre,
                      descripcion: archivo.descripcion || '',
                      archivoId: archivo.Id,
                    };
                  }
                }
                return null;
              }) || [];

          const anexosCargados = await Promise.all(anexosPromises);
          const anexosFiltrados = anexosCargados.filter((a) => a !== null);

          setEvidenceData((prev) => ({
            ...prev,
            anexos: anexosFiltrados,
          }));
        } finally {
          setUiState((prev) => ({ ...prev, loadingArchivos: false }));
        }
      };
      loadArchivos();
    }
  }, [editingEvidence, isEditMode, user, dispatch]);

  const handleFieldChange = (field, value) => {
    setEvidenceData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFileDrop = (files) => {
    if (files.length > 0) {
      setEvidenceData((prev) => ({
        ...prev,
        archivo: files[0],
        archivoPrincipalCambiado: true,
      }));
    }
  };

  const handleRemoveAnexo = (id) => {
    setEvidenceData((prev) => ({
      ...prev,
      anexos: prev.anexos.filter((anexo) => anexo.id !== id),
    }));
  };

  const handleSave = async () => {
    if (!activeProcess?.Id) return notifyError('No hay un proceso activo seleccionado');
    if (!evidenceData.titulo.trim()) return notifyError('El título es obligatorio');
    if (!evidenceData.descripcion.trim()) return notifyError('La descripción es obligatoria');
    if (!evidenceData.archivo && !isEditMode) return notifyError('Debe seleccionar un archivo');
    if (selectedKpis.length === 0) return notifyError('Debe seleccionar al menos un KPI');

    try {
      await dispatch(
        saveEvidence(
          activeProcess.Id,
          activePermission?.permiso?.EntityId,
          activePermission?.sede?.codigo,
          evidenceData,
          selectedKpis,
          evidenceData.archivoPrincipalCambiado ? evidenceData.archivo : null,
          user,
          false,
          isEditMode ? evidenceId : null
        )
      );

      if (!isEditMode && unitsToDuplicate.length > 0) {
        for (const unit of unitsToDuplicate) {
          const [unitId, sedeId] = unit.split('-');
          await dispatch(
            saveEvidence(
              activeProcess.Id,
              unitId,
              sedeId,
              evidenceData,
              selectedKpis,
              evidenceData.archivoPrincipalCambiado ? evidenceData.archivo : null,
              user,
              true,
              null
            )
          );
        }
      }

      navigate('/backoffice/evidences');
    } catch (error) {
      console.error('Error al guardar evidencia:', error);
    }
  };

  const unitOptions = useMemo(() => {
    if (!permissions?.data) return [];
    return permissions.data
      .filter(
        (perm) =>
          perm.permiso.EntityId !== activePermission?.permiso.EntityId ||
          perm.sede?.Id !== activePermission?.sede?.Id
      )
      .map((perm) => {
        const unidad = perm.permiso.EntityId.trim();
        const sede = perm.sede?.codigo.trim();
        return { value: `${unidad}-${sede}`, label: `${unidad} - ${sede}` };
      });
  }, [permissions, activePermission]);

  const activeCriterio = useMemo(() => {
    if (selectedKpis.length === 0) return null;
    const selected = allKpis.find((kpi) => String(kpi.Id) === String(selectedKpis[0]));
    return selected?.criterioId || null;
  }, [selectedKpis, allKpis]);

  const selectedKpiData = allKpis.filter((kpi) => selectedKpis.includes(String(kpi.Id)));
  const firstKpi = selectedKpiData[0];

  if (isEditMode && loadingEditingEvidence) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Text>Cargando evidencia...</Text>
      </div>
    );
  }

  if (!saveEvidenceEnabled) {
    return <Navigate to="/backoffice/evidences" replace />;
  }

  return (
    <div>
      <section>
        <div className="bienvenida-header">
          <Group justify={'space-between'}>
            <Box>
              <Text size={rem(24)} fw={600} mb={4}>
                {isEditMode ? 'Editar Evidencia' : 'Nueva Evidencia'}
              </Text>
              <Text size="md">
                {isEditMode ? 'Modifica la evidencia existente' : 'Crea una nueva evidencia para múltiples KPIs'}
              </Text>
            </Box>
            <Group>
              <Button variant="default" leftSection={<IconArrowLeft size={18} />} onClick={() => navigate('/backoffice/evidences')}>
                Volver al dashboard
              </Button>
              <Button
                variant="default"
                leftSection={<IconInfoCircle size={18} />}
                onClick={() => setUiState((prev) => ({ ...prev, guideOpened: !prev.guideOpened }))}
              >
                {uiState.guideOpened ? 'Ocultar' : 'Mostrar'} ayuda
              </Button>
            </Group>
          </Group>
        </div>
        <Collapse in={uiState.guideOpened} transitionDuration={500}>
          <Card withBorder shadow="sm" radius={'md'} p={'lg'} style={{ borderLeftColor: '#e72166', borderLeftWidth: '4px' }}>
            <Text size="sm">
              Una evidencia es la documentación objetiva, verificable y estructurada que respalda la realización de una acción, proceso o resultado institucional…
            </Text>
            <Blockquote color="gray" iconSize={30} icon={<IconExclamationCircle />} mt={'lg'} p={'md'}>
              <Text size="sm">La evidencia debe ser autoexplicativa y autosuficiente…</Text>
            </Blockquote>
            <Text size="sm" mt={'xs'}>
              La evidencia debe ser presentada en un archivo PDF (Informe de Evidencia)…
            </Text>
          </Card>
        </Collapse>
      </section>

      <Container size={'lg'}>
        <Flex p={'md'} gap={'md'}>
          <Box flex={7}>
            <Stack>
              <Card withBorder p={'lg'} radius={'md'}>
                <Card.Section inheritPadding py={'xs'}>
                  <Text size={'lg'} fw={600}>1. Selección de KPIs</Text>
                  <Text size="sm">{isEditMode ? 'KPIs asociados a esta evidencia (no se pueden modificar)' : 'Selecciona los KPIs que esta evidencia va a respaldar'}</Text>
                </Card.Section>
                <Card.Section inheritPadding py={'xs'}>
                  <TextInput
                    placeholder="Buscar por nombre ..."
                    leftSection={<IconSearch size={18} />}
                    mb={'lg'}
                    value={uiState.searchTerm}
                    onChange={(e) => setUiState((prev) => ({ ...prev, searchTerm: e.currentTarget.value }))}
                  />

                  {selectedKpis.length > 0 && (
                    <Group justify="space-between" mb={'xs'}>
                      <Text size="sm" fw={450}>
                        {isEditMode ? 'KPIs asociados' : 'Seleccionados'}: {selectedKpis.length}
                      </Text>
                      {!isEditMode && (
                        <Anchor onClick={() => setSelectedKpis([])} size="sm">
                          Limpiar seleccion
                        </Anchor>
                      )}
                    </Group>
                  )}

                  <Box py={'md'}>
                    <ScrollArea h={300} offsetScrollbars>
                      <Checkbox.Group value={selectedKpis} onChange={isEditMode ? undefined : setSelectedKpis} disabled={isEditMode}>
                        <Stack gap="xs">
                          {allKpis
                            .filter((kpi) => (kpi.titulo ?? '').toLowerCase().includes(uiState.searchTerm.toLowerCase()))
                            .filter((kpi) => (!isEditMode ? true : selectedKpis.includes(String(kpi.Id))))
                            .map((kpi, index) => {
                              const isDisabled = activeCriterio && kpi.criterioId !== activeCriterio;
                              const actuallyDisabled = isDisabled || isEditMode;

                              return (
                                <Tooltip.Floating
                                  key={index}
                                  label="Solo se pueden seleccionar KPIs del mismo criterio"
                                  withArrow
                                  withinPortal
                                  disabled={!isDisabled}       // solo cuando se deshabilita por criterio
                                  openDelay={150}
                                  color='red.7'
                                >
                                  <Box component="span">
                                    <Checkbox.Card
                                      value={String(kpi.Id)}
                                      radius="md"
                                      className="checkbox-card"
                                      disabled={actuallyDisabled}
                                      style={{ display: 'block', cursor: actuallyDisabled ? 'not-allowed' : undefined }}
                                    >
                                      <Group wrap="nowrap" align="flex-start">
                                        <Checkbox.Indicator color="#e72166" />
                                        <div>
                                          <Text fw={500} lh={1.3}>{kpi.numeroCompleto}. {kpi.titulo}</Text>
                                          <Text mt={4} c="dimmed" size="xs">{kpi.descripcion}</Text>
                                        </div>
                                      </Group>
                                    </Checkbox.Card>
                                  </Box>
                                </Tooltip.Floating>
                              );
                            })}
                        </Stack>
                      </Checkbox.Group>
                    </ScrollArea>
                  </Box>
                </Card.Section>
              </Card>
              
              <Card withBorder radius="md" p="md" style={{ background: 'linear-gradient(50deg, #eef5fcff 20%, #c5e3ffff 100%)', borderColor: '#228be6' }}>
                <Group align="flex-start" gap="md">
                  <IconPlayerTrackPrev size={28} color="#228be6" style={{ flexShrink: 0, marginTop: 4 }} />
                  <div style={{ flex: 1 }}>
                    <Text fw={600} mb={'xs'}>Evidencias previas relacionadas</Text>
                    <Text size="sm">Se muestran evidencias del proceso anterior que refieren a los KPIs seleccionados.</Text>
                    <Text size="sm">( Se utilizara el proceso anterior solo si coincide con la matriz del pei actual ) </Text>
                    <Box mt={'sm'}>
                      {selectedKpis.length === 0 ? (
                        <Text size="sm" c="dimmed">Seleccioná al menos un KPI para buscar evidencias previas.</Text>
                      ) : loadingPrevEvidences ? (
                        <Text size="sm" c={'dimmed'} ta={'center'}>Buscando evidencias…</Text>
                      ) : !previousEvidences || previousEvidences.length === 0 ? (
                        <Text size="sm" c="dimmed">No se encontraron evidencias previas para los KPIs seleccionados.</Text>
                      ) : (
                        <Group gap="md">
                          {previousEvidences.map(ev => {
                            return (
                              <Tooltip label={ev.titulo} withArrow key={ev.Id}>
                                <ActionIcon
                                  variant="filled"
                                  aria-label="file"
                                  radius="xl"
                                  onClick={() => { setSelectedEvidence(ev); setEvidenceModalOpened(true); }}
                                  color="#E72166"
                                >
                                  <IconFileText style={{ height: '70%', width: '70%' }} />
                                </ActionIcon>
                              </Tooltip>
                            );
                          })}
                        </Group>
                      )}
                    </Box>
                  </div>
                </Group>
              </Card>

              <Card withBorder p={'lg'} radius={'md'}>
                <Card.Section inheritPadding py={'xs'}>
                  <Text size={'lg'} fw={600}>2. Información Básica</Text>
                  <Text size="sm">Completa los datos principales de la evidencia</Text>
                </Card.Section>
                <Card.Section inheritPadding py={'xs'}>
                  <Stack align="stretch" gap="md">
                    <TextInput
                      label="Titulo"
                      description='Escriba un título breve y descriptivo del documento que adjunta. Debe permitir identificar de qué acción, actividad o logro se trata.'
                      required
                      placeholder="Ej: “Informe de avance del programa de tutorías 2025”"
                      value={evidenceData.titulo}
                      onChange={(e) => handleFieldChange('titulo', e.currentTarget.value)}
                    />
                    <Textarea
                      label="Descripción"
                      description='Incluya una breve explicación del contenido o propósito del documento adjunto, señalando qué aspecto del objetivo estratégico refleja'
                      required
                      placeholder="Ej: “Documento que respalda la alianza estratégica para ampliar la inserción laboral de los egresados”"
                      autosize
                      minRows={2}
                      maxRows={5}
                      value={evidenceData.descripcion}
                      onChange={(e) => handleFieldChange('descripcion', e.currentTarget.value)}
                    />

                    {evidenceData.archivo ? (
                      <Card withBorder p="sm">
                        <Group justify="space-between" align="flex-start">
                          <Group gap="xs" style={{ flex: 1, minWidth: 0 }}>
                            <IconFileText style={{ flexShrink: 0, marginTop: 2 }} />
                            <Box style={{ flex: 1, minWidth: 0 }}>
                              <Text fw={500} style={{ wordBreak: 'break-word' }}>{evidenceData.archivo.name}</Text>
                              <Text size="xs" c="dimmed">{(evidenceData.archivo.size / 1024).toFixed(2)} KB</Text>
                            </Box>
                          </Group>
                          <Tooltip label="Eliminar archivo" withArrow position="bottom">
                            <ActionIcon
                              variant="subtle"
                              color="gray"
                              radius={'xl'}
                              style={{ flexShrink: 0 }}
                              onClick={() => setEvidenceData((prev) => ({ ...prev, archivo: null, archivoPrincipalCambiado: true }))}
                            >
                              <IconX style={{ width: '70%', height: '70%' }} />
                            </ActionIcon>
                          </Tooltip>
                        </Group>
                      </Card>
                    ) : uiState.loadingArchivos ? (
                      <Card withBorder p="sm" bg="gray.0">
                        <Center>
                          <Text size="sm" c="dimmed">Cargando archivo principal...</Text>
                        </Center>
                      </Card>
                    ) : (
                      <DropzoneButton onDrop={handleFileDrop} />
                    )}
                  </Stack>
                </Card.Section>
              </Card>

              {/* 3. Anexos */}
              <Card withBorder p={'lg'} radius={'md'}>
                <Card.Section inheritPadding py={'xs'}>
                  <Group justify="space-between" align="center">
                    <div>
                      <Text size={'lg'} fw={600}>3. Anexos</Text>
                      <Text size="sm">Completa los datos para los anexos de evidencia</Text>
                    </div>

                    {/* Ícono de ayuda (gris) con tooltip y toggle de tarjeta */}
                    <Tooltip label={attachmentsHelpOpen ? 'Ocultar Ayuda' : 'Mostrar Ayuda'} withArrow position="left">
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        radius="xl"
                        aria-label="Ayuda de anexos"
                        onClick={() => setAttachmentsHelpOpen((v) => !v)}
                      >
                        <IconHelpCircle size={20} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                </Card.Section>

                {/* Tarjeta de ayuda (estilo captura 2) */}
                <Collapse in={attachmentsHelpOpen}>
                  <Card
                    withBorder
                    radius="md"
                    p="md"
                    mx="md"
                    mb="sm"
                    style={{
                      background: '#f4f6f8',
                      borderLeft: '4px solid #e72166',
                    }}
                  >
                    <Text size="sm">{attachmentsHelpText}</Text>
                    <Text size="sm" c="gray.6" mt="xs">
                      Formatos de archivo aceptados: <b>PDF y URL</b>
                    </Text>
                  </Card>
                </Collapse>

                <Card.Section inheritPadding py={'xs'}>
                  <Stack gap="md">
                    <Button
                      className="btn-anexo"
                      variant="outline"
                      fullWidth
                      radius="md"
                      leftSection={<IconPlus size={16} />}
                      onClick={() => setAttachmentModalOpen(true)}
                      disabled={evidenceData.anexos.length >= 3}
                    >
                      {evidenceData.anexos.length >= 3
                        ? 'Límite alcanzado (3/3)'
                        : isEditMode
                        ? 'Agregar Nuevo Anexo'
                        : 'Agregar Anexo'}
                    </Button>

                    {uiState.loadingArchivos ? (
                      <Card withBorder p="sm" bg="gray.0">
                        <Center>
                          <Text size="sm" c="dimmed">Cargando anexos...</Text>
                        </Center>
                      </Card>
                    ) : evidenceData.anexos.length > 0 && (
                      <Box>
                        <Group justify="space-between" mb="xs">
                          <Text size="sm" fw={500}>{isEditMode ? 'Anexos actuales:' : 'Anexos agregados:'}</Text>
                          <Text size="xs" c={evidenceData.anexos.length >= 3 ? 'red' : 'dimmed'}>
                            {evidenceData.anexos.length}/3
                          </Text>
                        </Group>

                        <Stack gap="xs">
                          {evidenceData.anexos.map((anexo, index) => (
                            <Card key={anexo.id || index} withBorder p="sm" radius="md">
                              <Flex align="flex-start" gap="sm">
                                <IconFileText size={16} style={{ flexShrink: 0, marginTop: 2 }} />
                                <Box style={{ flex: 1, minWidth: 0 }}>
                                  <Text size="sm" fw={500} style={{ wordBreak: 'break-word' }}>
                                    {anexo.nombre}
                                  </Text>
                                  <Text size="xs" c="gray.5" style={{ wordBreak: 'break-word' }}>
                                    {anexo.tipo === 'url' ? 'URL' : 'Archivo'} - {anexo.descripcion}
                                  </Text>
                                </Box>
                                <Tooltip label={isEditMode ? 'Eliminar anexo existente' : 'Eliminar anexo'}>
                                  <ActionIcon
                                    variant="subtle"
                                    color="red"
                                    onClick={() => handleRemoveAnexo(anexo.id)}
                                    style={{ flexShrink: 0 }}
                                  >
                                    <IconTrash size={16} />
                                  </ActionIcon>
                                </Tooltip>
                              </Flex>
                            </Card>
                          ))}
                        </Stack>
                      </Box>
                    )}
                  </Stack>
                </Card.Section>
              </Card>

              {unitOptions.length > 0 && !isEditMode && (
                <Card withBorder radius="md" p="md" style={{ background: 'linear-gradient(50deg, #eef5fcff 20%, #c5e3ffff 100%)', borderColor: '#228be6' }}>
                  <Group align="flex-start" gap="md">
                    <IconCopyPlus size={28} color="#228be6" style={{ flexShrink: 0, marginTop: 4 }} />
                    <div style={{ flex: 1 }}>
                      <Text fw={600} mb={4}>¿Deseas replicar esta evidencia a otras unidades de gestión a tu cargo?</Text>
                      <Text size="sm" c="dimmed" mb="xs">
                        Puedes seleccionar una o más unidades de gestión que están bajo tu responsabilidad, donde también se guardará esta evidencia.
                      </Text>
                      <Text size="sm" c="dimmed" mb="xs">
                        <b>⚠ Atención</b>: Verifica que esta evidencia no contenga información específica (por ejemplo, datos de estudiantes, docentes u otros aspectos particulares) que corresponda solo a una unidad en particular.
                      </Text>
                      <MultiSelect
                        label="Unidades de gestión"
                        placeholder="Selecciona unidades de gestión"
                        data={unitOptions}
                        value={unitsToDuplicate}
                        onChange={setUnitsToDuplicate}
                        nothingFoundMessage="No tienes otras unidades disponibles"
                        clearable
                      />
                    </div>
                  </Group>
                </Card>
              )}

              <Group justify="end" mb={'md'} mt={'xs'}>
                <Button
                  size="md"
                  color="#e72166"
                  leftSection={<IconDeviceFloppy size={20} />}
                  onClick={handleSave}
                  loading={saving}
                  disabled={saving}
                >
                  {saving ? (isEditMode ? 'Actualizando...' : 'Guardando...') : (isEditMode ? 'Actualizar Evidencia' : 'Guardar Evidencia')}
                </Button>
              </Group>
            </Stack>
          </Box>

          {/* RIGHT PANEL */}
          <Box
            flex={4}
            style={{
              position: 'sticky',
              top: rem(2),
              alignSelf: 'flex-start',
              maxHeight: 'calc(100vh - 50px)',
              overflowY: 'auto',
            }}
          >
            <Stack>
              <Card withBorder p={'lg'} radius={'md'}>
                <Card.Section inheritPadding py={'xs'}>
                  <Text size={'lg'} fw={600}>{isEditMode ? 'Vista Previa de Edición' : 'Vista Previa'}</Text>
                </Card.Section>
                <Card.Section inheritPadding py={'xs'}>
                  <Stack gap={'sm'}>
                    <Box>
                      <Text size="xs" c={'dimmed'}>Titulo</Text>
                      <Text size="sm">{evidenceData.titulo || 'Sin titulo'}</Text>
                    </Box>
                    <Box>
                      <Text size="xs" c={'dimmed'}>Descripción</Text>
                      <Text size="sm">{evidenceData.descripcion || 'Sin descripción'}</Text>
                    </Box>
                    <Box>
                      <Text size="xs" c={'dimmed'}>Archivo PDF</Text>
                      <Text size="sm">{evidenceData.archivo ? evidenceData.archivo.name : 'No seleccionado'}</Text>
                    </Box>
                    <Box>
                      <Text size="xs" c={'dimmed'}>Anexos</Text>
                      <Text size="sm">{evidenceData.anexos.length > 0 ? `${evidenceData.anexos.length} anexos agregados` : 'Sin anexos'}</Text>
                    </Box>
                    <Box>
                      <Text size="xs" c={'dimmed'}>{isEditMode ? 'KPIs Asociados' : 'KPIs Seleccionados'}</Text>
                      <Text size="sm">
                        {selectedKpis.length > 0 ? `${selectedKpis.length} ${isEditMode ? 'KPIs asociados' : 'KPIs seleccionados'}` : 'Sin KPIs seleccionados'}
                      </Text>
                    </Box>
                  </Stack>
                </Card.Section>
              </Card>

              {selectedKpiData.length > 0 && (
                <Card withBorder p="lg" radius="md">
                  <Card.Section inheritPadding py="xs">
                    <Text size="lg" fw={600}>{isEditMode ? 'KPIs Asociados' : 'KPIs Seleccionados'}</Text>
                    <Text size="sm" c="dimmed">{isEditMode ? 'Detalle de los indicadores asociados a esta evidencia' : 'Detalle de los indicadores que respaldará esta evidencia'}</Text>
                  </Card.Section>
                  <Card.Section inheritPadding py="xs">
                    <Stack gap="xs">
                      <Group gap="xs">
                        <IconTarget size={16} color="#565290" />
                        <Text size="sm" fw={600} c="#565290">Objetivo: {firstKpi.objetivoNumero}</Text>
                      </Group>
                      <Text size="sm">{firstKpi.objetivoDescripcion}</Text>
                      <Group gap="xs" mt="xs">
                        <IconChartBar size={16} color="#8a4a97" />
                        <Text size="sm" fw={600} c="#8a4a97">Criterio: {firstKpi.criterioNumero}</Text>
                      </Group>
                      <Text size="sm">{firstKpi.criterioDescripcion}</Text>
                      <Divider my="sm" />
                    </Stack>

                    <Stack gap="sm">
                      <Text size="sm" fw={600} c="#e72166">Indicadores ({selectedKpiData.length})</Text>
                      {selectedKpiData.map((kpi) => (
                        <Card key={kpi.Id} withBorder radius="md" p="md" bg="gray.0">
                          <Group justify="space-between" align="start">
                            <div>
                              <Text size="sm" fw={600} c="dimmed">KPI-{kpi.numKpi}</Text>
                              <Text size="sm" fw={500} mt={4}>{kpi.titulo}</Text>
                              <Text size="xs" c="dimmed" mt={4}>{kpi.descripcion}</Text>
                              {kpi.ayuda && (
                                <>
                                  <Divider my={'xs'} />
                                  <Text size="xs" c="dimmed" mt={4}>{kpi.ayuda}</Text>
                                </>
                              )}
                            </div>
                          </Group>
                        </Card>
                      ))}
                    </Stack>
                  </Card.Section>
                </Card>
              )}
            </Stack>
          </Box>
        </Flex>
      </Container>

      <ModalEvidence
        opened={evidenceModalOpened}
        onClose={() => setEvidenceModalOpened(false)}
        evidence={selectedEvidence}
      />
      {/* Modal de Anexo */}
      <ModalAttachment
        opened={attachmentModalOpen}
        onClose={() => setAttachmentModalOpen(false)}
        onConfirm={(nuevoAnexo) => {
          setEvidenceData((prev) => ({
            ...prev,
            anexos: [...prev.anexos, nuevoAnexo],
          }));
          setAttachmentModalOpen(false);
        }}
        disabled={evidenceData.anexos.length >= 3}
      />
    </div>
  );
};

export default SaveEvidencesPage;
